import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { Product, ProductCategoryInfo } from '@/data/products'
import type { SupportedLanguage } from '@/i18n'

interface ProductCardProps {
  product: Product
  category: ProductCategoryInfo
  lang: SupportedLanguage
  index?: number
  showCategory?: boolean
}

export function ProductCard({ product, category, lang, index = 0, showCategory = true }: ProductCardProps) {
  const { t } = useTranslation()
  const productsPath = lang === 'en' ? 'products' : 'prodotti'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
    >
      <LocalizedLink
        to={`/${productsPath}/${product.slug[lang]}`}
        className="group relative flex flex-col h-full bg-white rounded-xl border border-surface-border overflow-hidden hover:border-accent/30 hover:shadow-xl transition-all"
      >
        {/* Accent bar bottom on hover */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />

        <div className="aspect-[4/3] bg-surface-light p-6 flex items-center justify-center">
          <img
            src={product.images[0]}
            alt={product.name}
            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="p-5 flex flex-col flex-1">
          {showCategory && (
            <span className="text-accent text-xs font-semibold uppercase tracking-wider mb-2">
              {t(category.nameKey)}
            </span>
          )}
          <h3 className="font-semibold text-lg text-text-heading group-hover:text-accent transition-colors mb-2 line-clamp-2">
            {product.name}
          </h3>
          <p className="text-sm text-text-muted line-clamp-2 mb-4">
            {t(product.descriptionKey)}
          </p>
          <div className="mt-auto flex items-center text-sm font-medium text-accent">
            {t('buttons.learnMore')}
            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </LocalizedLink>
    </motion.div>
  )
}
